import type {OmitStrict} from 'type-zoo'
import {valueOfCards} from '@kapitalist/contract/ruleset'
import type {TMillions} from '@kapitalist/contract/types/millions'
import {show$} from '@kapitalist/contract/utils/millions/copy'
import {MillionsMath} from '@kapitalist/contract/utils/millions/math'
import {skins} from '~/game/skins'
import type {TMinorActionDataPaySomething} from '~/game/types'
import {groupedCardDisplayNames} from '~/game/utils'
import {cardFactory} from '~/models/game/factories'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {TMinorActionUserFacingDescriptionData} from '~/models/game/MinorAction'
import MinorAction from '~/models/game/MinorAction'
import type {
  AnyLiquidatableCard,
  AnyPropertyCard,
  AnyValuableCard,
} from '~/models/game/types'
import {sentenceCase, xVerbY} from '~/utils/language'

type PublicMembersFromApi = OmitStrict<
  TMinorActionDataPaySomething,
  'type' | 'paidWith'
>

export default abstract class MinorActionPaySomething<
  PaySomethingType extends TMinorActionDataPaySomething,
>
  extends MinorAction<PaySomethingType>
  implements PublicMembersFromApi
{
  millionsOwed: TMillions
  private _paidWithCards: (AnyLiquidatableCard | AnyPropertyCard)[]

  get paidWithCards(): (AnyLiquidatableCard | AnyPropertyCard)[] {
    return this._paidWithCards
  }

  // TODO probably kill the setter and just make a copy of the model to modify it a la MW
  //  to maintain immutability.
  set paidWithCards(paidWith: AnyValuableCard[]) {
    // NB: since we create a MinorActionPaySomething with an empty paidWith[] when generating
    // a list of validResponses to a MinorActionPrompt, we want to be able to set
    // this property later. The API call will read `.datasource` though so we need to update both.
    this._paidWithCards = paidWith
    this.datasource.paidWith = paidWith.map(pw => pw.datasource)
  }

  constructor(data: PaySomethingType) {
    super(data)
    this.millionsOwed = data.millionsOwed
    this._paidWithCards = data.paidWith.map(lc => cardFactory(lc))
  }

  userFacingDescription(data: TMinorActionUserFacingDescriptionData): string {
    const {tense, skin, gamePosition, minorActor, majorActor} = data
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined

    const {conjugatedVerb, xNoun, yNoun} = xVerbY(
      tense,
      minorActor,
      tense === 'imperativePrompt' ? 'Pay' : 'pay',
      majorActor,
      currentPlayer?.id
    )

    // converts ['Pass Go', 'Pass Go', '$5M'] to ['2x Pass Go', '$5M']
    const cardList = groupedCardDisplayNames(this.paidWithCards, skin)

    const isPrompt = tense === 'imperativePrompt'
    return skins[skin].gameLogDescriptions.minorActionResponses.paySomething({
      tense,
      // in the prompt, paidWithCards isn't chosen yet, so drop the actor and show the
      // expected payment ('Pay Foo {expected}') rather than 'You paid Foo $0M ()'
      actor: isPrompt
        ? undefined
        : xNoun === minorActor.username
          ? xNoun
          : sentenceCase(xNoun),
      verb: conjugatedVerb,
      targetName: yNoun,
      amount: isPrompt
        ? show$(
            MillionsMath.min(minorActor.portfolio.totalValue(), this.millionsOwed)
          )
        : show$(valueOfCards(this.paidWithCards)),
      paidWithList: isPrompt ? undefined : `(${cardList.join(', ')})`,
    })
  }
}
